import { useEffect, useMemo } from "react";
import { useApp } from "@/context/AppContext";
import { Buildings, MapPin, ArrowSquareOut, ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

const COLUMNS = [
  { id: "saved", label: "SAVED", color: "#71717A" },
  { id: "applied", label: "APPLIED", color: "#38BDF8" },
  { id: "interview", label: "INTERVIEW", color: "#F59E0B" },
  { id: "offer", label: "OFFER", color: "#10B981" },
  { id: "rejected", label: "REJECTED", color: "#EF4444" },
];

const NEXT_STATUS = { saved: "applied", applied: "interview", interview: "offer" };

export default function KanbanBoard() {
  const { jobs, fetchJobs, updateJobStatus, setSelectedJob } = useApp();

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const grouped = useMemo(() => {
    const g = {};
    COLUMNS.forEach(c => { g[c.id] = []; });
    jobs.forEach(j => { if (g[j.status]) g[j.status].push(j); });
    return g;
  }, [jobs]);

  const total = COLUMNS.reduce((s, c) => s + (grouped[c.id]?.length || 0), 0);

  return (
    <div className="p-6 lg:p-8 h-full flex flex-col" data-testid="kanban-board">
      <div className="flex items-center justify-between mb-6 animate-in">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-white">Pipeline</h1>
          <p className="text-sm text-zinc-500 mt-1">{total} jobs in pipeline</p>
        </div>
      </div>

      <div className="flex-1 flex gap-4 overflow-x-auto pb-4 animate-in delay-1">
        {COLUMNS.map(col => (
          <div key={col.id} className="jp-kanban-col" data-testid={`kanban-col-${col.id}`}>
            <div className="jp-kanban-header flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: col.color }} />
                <span style={{ color: col.color }}>{col.label}</span>
              </div>
              <span className="font-mono text-xs px-2 py-0.5 rounded-full" style={{ color: col.color, background: `${col.color}15` }}>
                {grouped[col.id]?.length || 0}
              </span>
            </div>

            <ScrollArea className="flex-1 max-h-[calc(100vh-220px)]">
              <div className="p-2">
                {(grouped[col.id] || []).map(job => (
                  <div key={job.id} data-testid={`kanban-card-${job.id}`}
                    className="jp-kanban-card" onClick={() => setSelectedJob(job)}>
                    <div className="text-sm text-white truncate mb-1.5 font-medium">{job.title}</div>
                    <div className="flex items-center gap-1.5 mb-1">
                      <Buildings size={12} className="text-zinc-600" />
                      <span className="text-xs text-zinc-500 truncate">{job.company}</span>
                    </div>
                    {job.location && (
                      <div className="flex items-center gap-1.5 mb-2">
                        <MapPin size={12} className="text-zinc-700" />
                        <span className="text-[11px] text-zinc-600 truncate">{job.location}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-2.5 border-t border-white/5">
                      {job.scored ? (
                        <span className="font-mono text-xs font-semibold"
                          style={{ color: job.match_score >= 75 ? "#10B981" : job.match_score >= 50 ? "#F59E0B" : "#EF4444" }}>
                          {job.match_score}%
                        </span>
                      ) : (
                        <span className="text-[10px] text-zinc-700">unscored</span>
                      )}
                      <div className="flex items-center gap-1">
                        {NEXT_STATUS[col.id] && (
                          <Button size="sm" variant="ghost" data-testid={`advance-btn-${job.id}`}
                            className="h-6 px-2 text-zinc-600 hover:text-amber-500 hover:bg-amber-500/10 rounded-md text-[10px]"
                            onClick={(e) => { e.stopPropagation(); updateJobStatus(job.id, NEXT_STATUS[col.id]); }}>
                            <ArrowRight size={12} />
                          </Button>
                        )}
                        {job.job_url && (
                          <Button size="sm" variant="ghost"
                            className="h-6 w-6 p-0 text-zinc-700 hover:text-white rounded-md"
                            onClick={(e) => { e.stopPropagation(); window.open(job.job_url, '_blank'); }}>
                            <ArrowSquareOut size={12} />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {(grouped[col.id] || []).length === 0 && (
                  <div className="py-8 text-center text-zinc-700 text-xs">No jobs</div>
                )}
              </div>
            </ScrollArea>
          </div>
        ))}
      </div>
    </div>
  );
}
